/**
 * Mini-markdown-renderer. Wir bundeln KEINEN remark — zu schwer (~250kb).
 * Subset reicht für die wiki-section: headings, paragraphs, bullet/ordered
 * lists, blockquotes, bold/italic, code, links.
 *
 * Output ist HTML-string — caller rendert via `dangerouslySetInnerHTML`
 * NACHDEM er `escapeHtml` für untrusted-source aufgerufen hätte. Hier
 * unsere wiki-content ist build-time-trusted (alles aus content/wiki/),
 * also kein XSS-risiko.
 */

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function inline(text: string): string {
  let out = escapeHtml(text);
  // links [label](url)
  out = out.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-[color:var(--color-brand-accent)] underline">$1</a>'
  );
  // bold **text**
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  // italic *text*
  out = out.replace(/(^|\s)\*([^*]+)\*/g, "$1<em>$2</em>");
  // inline code `code`
  out = out.replace(/`([^`]+)`/g, '<code class="rounded bg-[color:var(--color-surface-sunken)] px-1 py-0.5 text-sm">$1</code>');
  return out;
}

export function renderMarkdown(md: string): string {
  const lines = md.split("\n");
  const out: string[] = [];
  let inList: "ul" | "ol" | null = null;
  let inQuote = false;
  let paragraphBuf: string[] = [];

  const flushParagraph = () => {
    if (paragraphBuf.length === 0) return;
    out.push(`<p>${inline(paragraphBuf.join(" "))}</p>`);
    paragraphBuf = [];
  };
  const closeList = () => {
    if (inList) {
      out.push(`</${inList}>`);
      inList = null;
    }
  };
  const closeQuote = () => {
    if (inQuote) {
      out.push("</blockquote>");
      inQuote = false;
    }
  };

  for (const rawLine of lines) {
    const line = rawLine.replace(/\s+$/, "");
    if (line === "") {
      flushParagraph();
      closeList();
      closeQuote();
      continue;
    }
    const hMatch = line.match(/^(#{1,4})\s+(.+)$/);
    if (hMatch) {
      flushParagraph();
      closeList();
      closeQuote();
      const level = hMatch[1]!.length;
      const cls =
        level === 2
          ? "mt-6 text-2xl font-semibold"
          : level === 3
            ? "mt-4 text-xl font-semibold"
            : level === 4
              ? "mt-3 text-lg font-semibold"
              : "mt-6 text-3xl font-bold";
      out.push(`<h${level} class="${cls}">${inline(hMatch[2]!)}</h${level}>`);
      continue;
    }
    const ulMatch = line.match(/^[-*]\s+(.+)$/);
    if (ulMatch) {
      flushParagraph();
      closeQuote();
      if (inList !== "ul") {
        closeList();
        out.push('<ul class="ml-6 list-disc space-y-1">');
        inList = "ul";
      }
      out.push(`<li>${inline(ulMatch[1]!)}</li>`);
      continue;
    }
    const olMatch = line.match(/^\d+\.\s+(.+)$/);
    if (olMatch) {
      flushParagraph();
      closeQuote();
      if (inList !== "ol") {
        closeList();
        out.push('<ol class="ml-6 list-decimal space-y-1">');
        inList = "ol";
      }
      out.push(`<li>${inline(olMatch[1]!)}</li>`);
      continue;
    }
    const quoteMatch = line.match(/^>\s?(.*)$/);
    if (quoteMatch) {
      flushParagraph();
      closeList();
      if (!inQuote) {
        out.push('<blockquote class="my-3 border-l-4 border-[color:var(--color-brand-primary)] bg-[color:var(--color-surface-elevated)] p-3 italic">');
        inQuote = true;
      }
      out.push(`<p>${inline(quoteMatch[1]!)}</p>`);
      continue;
    }
    paragraphBuf.push(line);
  }
  flushParagraph();
  closeList();
  closeQuote();
  return out.join("\n");
}
